import { oc } from "@orpc/contract";
import { AuditModule, EXPORT_FORMATS } from "@erp/shared-types";
import { z } from "zod";

const AuditReportEventSchema = z.object({
  id: z.string(),
  timestamp: z.string(),
  actionType: z.string(),
  actor: z.object({
    id: z.string(),
    name: z.string(),
    type: z.enum(["user", "system", "ai"]),
  }),
  entityType: z.string(),
  entityId: z.string(),
  entityLabel: z.string(),
  purchaseOrderNumber: z.string().nullish(),
  salesOrderNumber: z.string().nullish(),
  summary: z.string(),
  details: z.record(z.string(), z.unknown()).optional(),
});

/** Trims whitespace and converts blank strings to undefined. */
const optionalTrimmedString = z
  .string()
  .optional()
  .transform((value) => {
    if (typeof value !== "string") return undefined;
    const trimmed = value.trim();
    return trimmed === "" ? undefined : trimmed;
  });

export const GenerateAuditReportInputSchema = z.object({
  module: z.nativeEnum(AuditModule),
  organizationId: z.string().optional(),
  formats: z.array(z.enum(EXPORT_FORMATS)),
  filters: z
    .object({
      dateRange: z
        .object({
          from: z.string().datetime(),
          to: z.string().datetime(),
        })
        .optional(),
      salesOrderId: optionalTrimmedString,
      salesOrderNumbers: z.array(z.string()).optional(),
      purchaseOrderNumbers: z.array(z.string()).optional(),
    })
    .refine(
      (f) =>
        f.dateRange ||
        f.salesOrderId ||
        (f.salesOrderNumbers && f.salesOrderNumbers.length > 0) ||
        (f.purchaseOrderNumbers && f.purchaseOrderNumbers.length > 0),
      {
        message:
          "At least one filter is required: dateRange, salesOrderId, salesOrderNumbers, or purchaseOrderNumbers",
      },
    ),
});

const GenerateAuditReportOutputSchema = z.object({
  module: z.string(),
  dateRange: z.object({ from: z.string(), to: z.string() }).optional(),
  generatedAt: z.string(),
  totalEvents: z.number(),
  events: z.array(AuditReportEventSchema),
  filters: z
    .object({
      salesOrderNumbers: z.array(z.string()).optional(),
      purchaseOrderNumbers: z.array(z.string()).optional(),
    })
    .optional(),
  exports: z.object({
    csv: z.string().optional(),
    pdf: z.string().optional(),
  }),
});

export const auditReportsContracts = {
  generate: oc
    .input(GenerateAuditReportInputSchema)
    .output(GenerateAuditReportOutputSchema)
    .route({
      method: "POST",
      summary: "Generate an order audit report",
      description:
        "Builds an audit trail of order events (ingestion, drafts, approvals, changes) filtered by date range, sales order or purchase order numbers. Returns events plus optional CSV and PDF exports.",
    }),
} as const;

export type GenerateAuditReportInput = z.infer<
  typeof GenerateAuditReportInputSchema
>;
export type GenerateAuditReportOutput = z.infer<
  typeof GenerateAuditReportOutputSchema
>;
export type AuditReportEventOutput = z.infer<typeof AuditReportEventSchema>;
